import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Check, Sparkles, Shield, Users, MessageCircle, Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API_URL = "https://api.recoverymatch.com";

const BetaSignupPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);

  // Fetch waitlist count on load
  React.useEffect(() => {
    fetch(`${API_URL}/api/beta/stats`)
      .then(res => res.json())
      .then(data => setWaitlistCount(data.total_signups || 0))
      .catch(() => setWaitlistCount(50)); // Fallback
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/beta/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success("You're on the list! 🎉");
      } else {
        toast.error(data.detail || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to sign up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">You're In! 🎉</h1>
            <p className="text-gray-600 mb-6">
              Thanks for signing up for the RecoveryMatch beta! We'll send you an email with your exclusive access code and download link soon.
            </p>
            <div className="bg-purple-50 rounded-xl p-4 mb-6">
              <p className="text-purple-700 font-medium">Your Beta Perks:</p>
              <ul className="text-purple-600 text-sm mt-2 space-y-1">
                <li>✨ Early access to the app</li>
                <li>💜 1 month FREE of Recovery+</li>
                <li>⭐ Exclusive "Beta Tester" badge</li>
              </ul>
            </div>
            <p className="text-sm text-gray-500">
              Know someone else in recovery? Share this page with them!
            </p>
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied!");
              }}
              variant="outline"
              className="mt-4"
            >
              Copy Signup Link
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="p-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Heart className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            <span className="text-xl font-bold">RecoveryMatch</span>
          </div>
          <Button 
            variant="outline" 
            className="text-white border-white/30 hover:bg-white/10"
            onClick={() => navigate("/login")}
          >
            Log In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-sm">Beta Program Now Open</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Find Love in
              <span className="text-yellow-400"> Recovery</span>
            </h1>
            
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              The first dating app built specifically for the recovery community. 
              Connect with people who understand your journey.
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-purple-100">Safe & Sober Space</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-purple-100">Recovery Focused</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-purple-100">12-Step Compatible</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-purple-100">Meaningful Connections</span>
              </div>
            </div>
          </div>

          {/* Right - Signup Form */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl blur-3xl opacity-20"></div>
            <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Join the Beta</h2>
                <p className="text-gray-500 mt-1">Get early access + exclusive perks</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Your first name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl"
                    required
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-xl text-lg font-semibold"
                >
                  {loading ? "Signing up..." : (
                    <>
                      Get Early Access
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Social Proof */}
              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-gray-500 text-sm">
                  Join <span className="font-semibold text-purple-600">{waitlistCount !== null ? waitlistCount + 47 : '...'}</span> others on the waitlist
                </p>
              </div>

              {/* Beta Perks */}
              <div className="mt-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 text-sm mb-3">Beta Testers Get:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    Early access before public launch
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    1 month FREE of Recovery+ subscription
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    Exclusive "Beta Tester" badge
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    Direct input on app features
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-4 py-8 text-center">
        <p className="text-purple-300 text-sm">
          © 2026 RecoveryMatch. Built with 💜 for the recovery community.
        </p>
      </footer>
    </div>
  );
};

export default BetaSignupPage;
